import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, ViewChild } from '@angular/core';
import { Group } from "@app/tool/model/group.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { TranslateService } from "@ngx-translate/core";
import { ToolSet } from "@app/tool/model/toolSet.model";
import { Tool } from "@app/tool/model/tool.model";
import { Line } from "@models/line.model";
import { EquipmentService } from "@services/equipment.service";
import { PartNumber } from "@models/partNumber.model";
import { PartNumbersService } from "@services/part-numbers.service";
import { map } from "rxjs/operators";
import { TableComponent } from "@components/custom-table/table.component";

@Component({
    selector: 'app-tool-request-table',
    templateUrl: './tool-request-table.component.html',
    styleUrls: ['./tool-request-table.component.scss']
})
export class ToolRequestTableComponent implements OnChanges {

    @ViewChild(TableComponent, {static: false}) table: TableComponent;

    @Input() toolsGrouped = new Array<Group>();
    @Input() selectedTools = new Array<Tool | ToolSet>();
    @Input() selectedLine: Line;
    @Input() tableConfig: TableConfig;
    @Input() isLoading = false;
    @Input() showFilters = true;
    @Input() showGroupFilters = false;
    @Input() showGroupers = false;
    @Input() isFilterExpanded = false;
    @Input() isMaterial = false;
    @Input() hasTitle = false;
    @Input() allExpanded = false;

    @Output() pageChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterGroupChange = new EventEmitter<any>();
    @Output() groupChange = new EventEmitter<any>();
    @Output() sortChange = new EventEmitter<any>();
    @Output() onToolClicked = new EventEmitter<{ checked: boolean, group: Tool | ToolSet }>();

    groupableColumns = [];
    groupFilters = [];

    constructor(private translateService: TranslateService,
                private partNumberService: PartNumbersService,
                private equipmentService: EquipmentService) {
        this.groupFilters = this.buildGroupFilters();
        this.groupableColumns = this.buildGroupableColumnKeys();
        this.translateService.onLangChange.subscribe((event) => {
            this.groupFilters = this.buildGroupFilters();
            this.rebuildAllOptionForGroupFilter();
            this.groupableColumns = this.buildGroupableColumnKeys();
        });
    }

    buildGroupFilters(items = [], id = -1) {
        return [
            {
                id: 0,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.equipment"),
                items: id === 0 && items.length > 0 ? items : ((this.groupFilters.length > 0 && this.groupFilters[0].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "equipmentId",
                selectedValue: (this.groupFilters.length > 0 && this.groupFilters[0].selectedValue) ?
                    this.groupFilters[0].selectedValue : '',
            },
            {
                id: 1,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.partNumber"),
                items: id === 1 && items.length > 0 ? items : ((this.groupFilters.length > 1 && this.groupFilters[1].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "partNumberId",
                selectedValue: (this.groupFilters.length > 1 && this.groupFilters[1].selectedValue) ?
                    this.groupFilters[1].selectedValue : '',
            },
        ];
    }

    buildGroupableColumnKeys() {
        return [
            {
                id: 0,
                name: this.translateService.instant("admin.tools.table.grouping.options.equipment"),
                value: "Equipment"
            },
            {
                id: 1,
                name: this.translateService.instant("admin.tools.table.grouping.options.partNumber"),
                value: "PartNumber"
            }
        ];
    }

    ngOnChanges(changes: { [propName: string]: SimpleChange }) {
        if (changes['selectedLine'] && changes['selectedLine'].previousValue !== changes['selectedLine'].currentValue
            && !changes['selectedLine'].firstChange) {
            this.getEquipments();
            this.getPartNumbersByLine();
        }
    }

    private addAllOptionForSelect(response, itemsName) {
        response[itemsName].unshift({
            id: 0,
            name: this.translateService.instant('lineSetter.toolRequest.table.groupFilter.all.' + itemsName)
        });
        response.total++;
        return response;
    }

    private rebuildAllOptionForGroupFilter() {
        if (this.groupFilters.length > 0 && this.groupFilters[0].items.length > 0) {
            this.groupFilters[0].items[0].name = this.translateService.instant(
                'lineSetter.toolRequest.table.groupFilter.all.equipment');
        }
        if (this.groupFilters.length > 1 && this.groupFilters[1].items.length > 0) {
            this.groupFilters[1].items[0].name = this.translateService.instant(
                'lineSetter.toolRequest.table.groupFilter.all.items');
        }
    }

    private getEquipments() {
        this.isLoading = true;
        this.equipmentService.getAllEquipmentByLine([this.selectedLine]).pipe(map(
            res => this.addAllOptionForSelect(res, 'equipment')
        )).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.equipment, 0);
                this.isLoading = false;
            });
    }

    private getPartNumbersByLine() {
        this.isLoading = true;
        this.partNumberService.getPartNumbersByLine(this.selectedLine).pipe(map(
            res => this.addAllOptionForSelect(res, 'items')
        )).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.items, 1);
                this.isLoading = false;
            });
    }

    onPageChange(tableConfig: TableConfig) {
        this.pageChange.emit(tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this.sortChange.emit(tableConfig);
    }

    onFilterChange(tableConfig: TableConfig) {
        this.filterChange.emit(tableConfig);
    }

    onFilterGroupChange(tableConfig: TableConfig) {
        for (const filter of this.tableConfig.filters) {
            if (filter.field === 'equipmentId') {
                this.groupFilters[0].selectedValue = filter.value;
            }
            if (filter.field === 'partNumberId') {
                this.groupFilters[1].selectedValue = filter.value;
            }
        }
        this.filterGroupChange.emit(tableConfig);
    }

    onGroupChange(tableConfig: TableConfig) {
        this.groupChange.emit(tableConfig);
    }

    isToolSet(record) {
        return record instanceof ToolSet;
    }

    getSetForTool(tool: Tool): ToolSet | null {
        for (const el of this.toolsGrouped) {
            if (el instanceof ToolSet) {
                for (const toolOfSet of el.tools) {
                    if (toolOfSet.id === tool.id) {
                        return el;
                    }
                }
            }
        }
        return null;
    }

    isToolSelected(tool: Tool | ToolSet) {
        return this.selectedTools.filter(selectedTool => (
                (selectedTool instanceof Tool && tool instanceof Tool && selectedTool.id === tool.id) ||
                (selectedTool instanceof ToolSet && tool instanceof ToolSet && selectedTool.name === tool.name)
            )
        ).length > 0;
    };

    private handleSubtoolSelected(toolSet: ToolSet) {
        const selectedSubtools = toolSet.tools.filter(tool => this.isToolSelected(tool));
        for (const selectedSubTool of selectedSubtools) {
            this.onToolClicked.emit({checked: false, group: selectedSubTool})
        }
    }

    private handleToolChecked(selectedTool: Tool, setForSelectedTool: ToolSet) {
        if (setForSelectedTool) {
            let allChecked = true;
            for (const tool of setForSelectedTool.tools) {
                if (tool.id !== selectedTool.id && !this.isToolSelected(tool)) {
                    allChecked = false;
                }
            }
            if (allChecked) {
                for (const tool of setForSelectedTool.tools) {
                    if (this.isToolSelected(tool)) {
                        this.onToolClicked.emit({checked: false, group: tool});
                    }
                }
                this.onToolClicked.emit({checked: true, group: setForSelectedTool});
            } else {
                this.onToolClicked.emit({checked: true, group: selectedTool});
            }
        } else {
            this.onToolClicked.emit({checked: true, group: selectedTool});
        }
    }

    private handleToolUnchecked(unselectedTool, setForUnselectedTool) {
        if (setForUnselectedTool && this.isToolSelected(setForUnselectedTool)) {
            this.onToolClicked.emit({checked: false, group: setForUnselectedTool});
            for (const selectedTool of setForUnselectedTool.tools) {
                if (selectedTool.id !== unselectedTool.id) {
                    this.onToolClicked.emit({checked: true, group: selectedTool});
                }
            }
        } else {
            this.onToolClicked.emit({checked: false, group: unselectedTool});
        }
    }

    onClickTool(event: any, record: Tool | ToolSet) {
        event.stopPropagation();
        if (record instanceof ToolSet) {
            this.handleSubtoolSelected(record);
            this.onToolClicked.emit({checked: event.target.checked, group: record});
        } else if (record instanceof Tool) {
            const setForSelectedTool = this.getSetForTool(record);
            if (event.target.checked) {
                this.handleToolChecked(record, setForSelectedTool)
            } else {
                this.handleToolUnchecked(record, setForSelectedTool);
            }
        }
    }

    isSelectionDisabled(record) {
        return record.disabled;
    }

    hasCheckbox(record: any) {
        return (record instanceof Tool || record instanceof ToolSet);
    }

    toolSetContainsTool(tool: Tool, toolSet: ToolSet): boolean {
        return toolSet.tools.filter(toolFromToolSet => toolFromToolSet.id === tool.id).length > 0;
    }

    isChecked(record: Tool | ToolSet) {
        if (this.isToolSelected(record)) {
            return true;
        } else if (record instanceof Tool) {
            return this.selectedTools.filter(
                tool => tool instanceof ToolSet &&
                    this.toolSetContainsTool(record, tool)
            ).length > 0;
        }
    }

    toggleRowExpansions(expanded: boolean) {
        this.table.onExpandAll(expanded);
    }

}
